
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../src/libs/supabase';
import { sendEmail } from '../../src/libs/sendEmail';
import {
  ArrowRight, ArrowLeft, Check,
  Phone, MessageSquare, FolderOpen, Mail, Database, Clock, ChevronRight,
  PieChart, Timer, Plus, Minus,
  Calculator, Target, X, Loader2, ChevronDown
} from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { Reveal } from '../Reveal';

// --- TYPES ---
type Step = 'industry' | 'status' | 'goals' | 'speed' | 'team' | 'activity' | 'result';

interface AuditState {
  industry: string;
  status: string[]; // Multi-Select
  goals: string[];
  speed: string;
  employees: number;
  salary: number;
  emails: number;
  dataRecords: number;
  searchMinutes: number;
  meetings: number; // New: Protokolle & Notes
  filing: number;   // New: Ablage & Suchen
}

interface ContactForm {
  firstName: string;
  lastName: string;
  company: string;
  phone: string;
  email: string;
  note: string;
}

// --- CONSTANTS ARE NOW TRANSLATION-BASED ---
// INDUSTRIES and GOALS are fetched from translations in renderStepContent()

// --- HELPER: Render Text with Bold Markers ---
const renderWithBold = (text: string) => {
  // Safety check - if text is not a string, return it as-is
  if (typeof text !== 'string') return text || '';
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index} className="font-bold text-gray-900">{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
};

// --- SUB-COMPONENTS ---

const ROIHero = ({ onStart }: { onStart: () => void }) => {
  const { t } = useTranslation();
  return (
    <section className="pt-32 pb-20 bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <Reveal>
          <div className="inline-block px-4 py-2 bg-gray-100 text-gray-900 text-[10px] font-bold uppercase tracking-[0.2em] mb-8 border border-gray-200">
            {t('shared.roi.hero.badge')}
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-gray-900 tracking-tighter leading-[0.95] mb-8">
            {t('shared.roi.hero.title_part1')} <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rabbit-navy to-gray-500">
              {t('shared.roi.hero.title_part2')}
            </span>
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-12 font-light leading-relaxed">
            {t('shared.roi.hero.subtitle')}
          </p>
          <button
            onClick={onStart}
            className="bg-[#0B1121] text-white px-10 py-5 font-bold uppercase tracking-widest text-xs hover:bg-[#1a253a] transition-colors shadow-2xl inline-flex items-center gap-3"
          >
            {t('shared.roi.hero.cta')} <ChevronDown size={16} />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

const ROIQuoteSection = () => {
  const { t } = useTranslation();
  return (
    <section className="w-full bg-[#0B1121] text-white py-32 border-t border-gray-800 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        <Reveal>
          <div className="inline-block px-4 py-2 bg-white/5 text-emerald-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-12 border border-white/10">
            {t('shared.roi.quote.badge')}
          </div>

          <h2
            className="text-3xl md:text-5xl font-light leading-[1.4] mb-12 font-sans tracking-tight text-gray-200"
            dangerouslySetInnerHTML={{ __html: t('shared.roi.quote.text') }}
          />

          <div className="flex flex-col items-center justify-center gap-2">
            <div className="w-12 h-1 bg-emerald-500 mb-4"></div>
            <div className="font-bold text-white tracking-wide text-lg">
              {t('shared.roi.quote.author')}
            </div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-widest">
              {t('shared.roi.quote.role')}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const ROIProcess = ({ onOpenModal }: { onOpenModal: () => void }) => {
  const { t } = useTranslation();
  const getPointsArray = (key: string, fallback: string[]) => {
    const result = t(key, { returnObjects: true });
    return Array.isArray(result) ? result as string[] : fallback;
  };

  const steps = [
    {
      id: t('shared.roi.process.steps.step1.id') || '01',
      title: t('shared.roi.process.steps.step1.title') || 'Diagnosis & Goal Setting',
      subtitle: t('shared.roi.process.steps.step1.subtitle') || 'INITIAL SITUATION',
      desc: t('shared.roi.process.steps.step1.desc') || '',
      points: getPointsArray('shared.roi.process.steps.step1.points', ['PROCESS COST ANALYSIS', 'IDENTIFICATION OF BOTTLENECKS', 'DEFINITION OF GOALS'])
    },
    {
      id: t('shared.roi.process.steps.step2.id') || '02',
      title: t('shared.roi.process.steps.step2.title') || 'Solution Development',
      subtitle: t('shared.roi.process.steps.step2.subtitle') || 'STRATEGIC CONCEPT',
      desc: t('shared.roi.process.steps.step2.desc') || '',
      points: getPointsArray('shared.roi.process.steps.step2.points', ['SMART DOCUMENT MANAGEMENT', 'RISK-FREE AUTOMATION', 'INTEGRATION'])
    },
    {
      id: t('shared.roi.process.steps.step3.id') || '03',
      title: t('shared.roi.process.steps.step3.title') || 'Implementation',
      subtitle: t('shared.roi.process.steps.step3.subtitle') || 'INTEGRATION',
      desc: t('shared.roi.process.steps.step3.desc') || '',
      points: getPointsArray('shared.roi.process.steps.step3.points', ['SHADOW OPERATION', 'PARALLEL DEPLOYMENT', 'GRADUAL ROLLOUT'])
    },
    {
      id: t('shared.roi.process.steps.step4.id') || '04',
      title: t('shared.roi.process.steps.step4.title') || 'Effect & Scaling',
      subtitle: t('shared.roi.process.steps.step4.subtitle') || 'VALUE ADD',
      desc: t('shared.roi.process.steps.step4.desc') || '',
      points: getPointsArray('shared.roi.process.steps.step4.points', ['RECOVERY OF TIME', 'ERROR REDUCTION', 'KNOWLEDGE BASE'])
    }
  ];

  return (
    <section className="py-32 bg-gray-50 relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <Reveal>
          <div className="flex flex-col md:flex-row justify-between items-end mb-32 border-b border-gray-200 pb-8">
            <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
              {(t('shared.roi.process.title') || 'TRANSFORMATION PROCESS').toString().split(' ')[0]}<br />{(t('shared.roi.process.title') || 'TRANSFORMATION PROCESS').toString().split(' ').slice(1).join(' ')}
            </h2>
            <div className="text-right mt-8 md:mt-0">
              <p className="text-gray-500 text-lg max-w-md">
                {t('shared.roi.process.subtitle')}
              </p>
            </div>
          </div>
        </Reveal>

        <div className="relative">
          <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-gray-300 -translate-x-1/2"></div>
          <div className="space-y-32">
            {steps.map((step, index) => (
              <Reveal key={step.id} delay={index * 0.1}>
                <div className={`flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-0 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                  <div className={`md:w-1/2 ${index % 2 !== 0 ? 'md:pl-24' : 'md:pr-24'} pl-16 w-full`}>
                    <div className="relative">
                      <span className="text-xs font-bold uppercase tracking-widest text-clever-teal mb-2 block">{step.subtitle}</span>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                      <p className="text-gray-500 leading-relaxed mb-6">{step.desc}</p>
                      <div className="flex flex-wrap gap-2">
                        {step.points.map((pt, idx) => (
                          <span key={idx} className="px-2 py-1 bg-white text-gray-600 text-[10px] font-bold uppercase tracking-wider border border-gray-200">
                            {pt}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="absolute left-[19px] md:left-1/2 -translate-x-1/2 flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-clever-teal ring-8 ring-gray-50 z-10"></div>
                  </div>
                  <div className="hidden md:block md:w-1/2" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.4}>
          <div className="mt-32 text-center">
            <button
              onClick={onOpenModal}
              className="bg-rabbit-navy text-white px-12 py-6 font-bold uppercase tracking-widest text-sm hover:bg-gray-900 transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-1"
            >
              {t('shared.roi.process.cta')}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const ROIFAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqsResult = t('shared.roi.faq.items', { returnObjects: true });
  const faqs = Array.isArray(faqsResult) ? faqsResult as Array<{ id: string, q: string, a: string }> : [
    { id: '01', q: 'How accurate is the calculation?', a: 'The calculator gives you a conservative estimate based on industry benchmarks and your specific inputs.' },
    { id: '02', q: 'Is the data I enter stored?', a: 'No. Your data is only processed locally in the browser for calculation. Nothing is stored.' },
    { id: '03', q: 'What happens after the calculation?', a: 'You receive a summary of your potential. You can optionally request a free in-depth analysis.' },
    { id: '04', q: 'Which industries does this work for?', a: 'The calculator is designed for knowledge workers across all industries.' }
  ];

  return (
    <section id="faq" className="py-32 bg-white border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
            <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
              ROI<br />FAQ
            </h2>
          </div>
        </Reveal>
        <div className="flex flex-col border-t border-gray-200">
          {faqs.map((faq, index) => (
            <Reveal key={index} delay={index * 0.1}>
              <div className={`group border-b border-gray-200 transition-colors duration-300 ${openIndex === index ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                <button
                  className="w-full flex items-start md:items-center justify-between py-10 px-4 md:px-8 text-left"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-12">
                    <span className="font-mono text-xs text-gray-400 uppercase tracking-widest">// {faq.id}</span>
                    <span className={`text-xl md:text-2xl font-bold transition-colors duration-300 ${openIndex === index ? 'text-clever-teal' : 'text-gray-900'}`}>{faq.q}</span>
                  </div>
                  <div className="ml-4 flex-shrink-0 text-gray-400 font-mono">{openIndex === index ? <Minus size={20} /> : <Plus size={20} />}</div>
                </button>
                <div className={`overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${openIndex === index ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-4 md:px-8 pb-10 grid grid-cols-1 md:grid-cols-12">
                    <div className="hidden md:block md:col-span-2"></div>
                    <div className="md:col-span-8">
                      <div className="text-lg leading-relaxed text-gray-600 font-light pl-4 border-l border-clever-teal/30">{faq.a}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CapacityShiftChart = ({ adminPercent, valuePercent }: { adminPercent: number, valuePercent: number }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white border border-gray-200 p-8 mt-8">
      <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-6">
        {t('shared.roi.calculator.steps.step7.capacity_shift')}
      </h4>
      <div className="mb-6">
        <div className="flex justify-between text-xs font-mono mb-2">
          <span className="text-gray-500">
            {t('shared.roi.calculator.steps.step7.current_state')}
          </span>
          <span className="text-clever-teal font-bold">
            {Math.round(adminPercent)}% {t('shared.roi.calculator.steps.step7.routine_label')}
          </span>
        </div>
        <div className="w-full h-8 flex">
          <div style={{ width: `${adminPercent}%` }} className="bg-gray-200 border-r border-white relative group">
            <div className="absolute inset-0 flex items-center justify-center text-[9px] text-gray-500 font-bold opacity-0 group-hover:opacity-100 transition-opacity">
              {t('shared.roi.calculator.steps.step7.admin_label')}
            </div>
          </div>
          <div style={{ width: `${valuePercent}%` }} className="bg-[#0B1121]"></div>
        </div>
      </div>
      <div>
        <div className="flex justify-between text-xs font-mono mb-2">
          <span className="text-gray-500">
            {t('shared.roi.calculator.steps.step7.target_state')}
          </span>
          <span className="text-emerald-600 font-bold">
            +{Math.round(adminPercent * 0.75)}% {t('shared.roi.calculator.steps.step7.new_capacity_label')}
          </span>
        </div>
        <div className="w-full h-8 flex">
          <div style={{ width: `${adminPercent * 0.25}%` }} className="bg-gray-200 border-r border-white"></div>
          <div style={{ width: `${valuePercent}%` }} className="bg-[#0B1121] border-r border-white"></div>
          <div style={{ width: `${adminPercent * 0.75}%` }} className="bg-emerald-400 relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.2)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.2)_50%,rgba(255,255,255,0.2)_75%,transparent_75%,transparent)] bg-[length:10px_10px]"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ROIPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isNavigating, setIsNavigating] = useState(false);
  const [formData, setFormData] = useState<AuditState>({
    industry: '',
    status: [],
    goals: [],
    speed: '',
    employees: 0,
    salary: 0,
    emails: 0,
    dataRecords: 0,
    searchMinutes: 0,
    meetings: 0,
    filing: 0
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [contactForm, setContactForm] = useState<ContactForm>({
    firstName: '', lastName: '', company: '', phone: '', email: '', note: ''
  });

  const annualCostPerEmployee = formData.salary * 14 * 1.32;
  const totalPayroll = annualCostPerEmployee * formData.employees;
  const workingDays = 210;
  const dailyHours = 8;
  const hourlyRate = annualCostPerEmployee > 0 ? annualCostPerEmployee / (workingDays * dailyHours) : 0;
  const minuteRate = hourlyRate / 60;
  const wastedMinutesPerDayPerEmployee = formData.emails + formData.dataRecords + formData.searchMinutes + formData.meetings + formData.filing;
  const dailyBurnRate = wastedMinutesPerDayPerEmployee * minuteRate * formData.employees;
  const annualBurnRate = dailyBurnRate * workingDays;
  const burnPercentage = totalPayroll > 0 ? Math.min((annualBurnRate / totalPayroll) * 100, 100) : 0;
  const productivePercentage = 100 - burnPercentage;
  const automationFactor = 0.75;
  const potentialSavings = annualBurnRate * automationFactor;
  const totalHoursWastedPerYear = (wastedMinutesPerDayPerEmployee / 60) * formData.employees * workingDays;
  const hoursSavedPerYear = Math.round(totalHoursWastedPerYear * automationFactor);
  const gainedFTE = isNaN(hoursSavedPerYear) || !isFinite(hoursSavedPerYear) ? 0 : hoursSavedPerYear / (workingDays * dailyHours);

  // Central Navigation Logic with Delay & Auto-Scroll
  const navigateToStep = (stepNumber: number) => {
    if (isNavigating) return;
    setIsNavigating(true);

    // Slight delay to feel controlled
    setTimeout(() => {
      setCurrentStep(stepNumber);
      setIsNavigating(false);
      // Ensure the clickable area is fully visible by scrolling to the container
      const element = document.getElementById('calculator-start');
      if (element) {
        const offset = 100; // Account for fixed header
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 400);
  };

  const nextStep = () => { if (currentStep < 7) navigateToStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 1) navigateToStep(currentStep - 1); };

  const handleMultiSelect = (item: string, field: keyof AuditState) => {
    const currentList = formData[field] as string[];
    if (currentList.includes(item)) {
      setFormData({ ...formData, [field]: currentList.filter(i => i !== item) });
    } else {
      setFormData({ ...formData, [field]: [...currentList, item] });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const { error } = await supabase.from('leads').insert([{
        company: contactForm.company,
        email: contactForm.email,
        sector: 'ROI Calculator',
        details: `Name: ${contactForm.firstName} ${contactForm.lastName}\nPhone: ${contactForm.phone}\nPotential Savings: € ${Math.round(potentialSavings).toLocaleString()}`
      }]);

      if (error) throw error;

      sendEmail({
        email: contactForm.email,
        name: `${contactForm.firstName} ${contactForm.lastName}`,
        subject: 'New ROI Calculator Lead',
        message: `New ROI Calculator Lead:\nName: ${contactForm.firstName} ${contactForm.lastName}\nCompany: ${contactForm.company}\nEmail: ${contactForm.email}\nPhone: ${contactForm.phone}\n\nCalculated Data:\nEmployees: ${formData.employees}\nAvg Salary: ${formData.salary}\nEst. Annual Burn Rate: € ${Math.round(annualBurnRate).toLocaleString()}\nPotential Savings: € ${Math.round(potentialSavings).toLocaleString()}`,
        lang: i18n.language
      }).catch(console.error);

      setFormStatus('success');
    } catch (err) {
      console.error(err);
      alert(t('common.form_error'));
      setFormStatus('idle');
    }
  };

  const scrollToCalculator = () => {
    const element = document.getElementById('calculator-start');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getPsychologicalProfile = () => {
    const employees = formData.employees;
    const totalMins = wastedMinutesPerDayPerEmployee;

    const diagnosis = t('shared.roi.calculator.results.diagnosis', { employees, minutes: totalMins });
    const impact = t('shared.roi.calculator.results.impact', {
      burn: Math.round(annualBurnRate).toLocaleString(),
      savings: Math.round(potentialSavings).toLocaleString()
    });
    const strategy = t('shared.roi.calculator.results.strategy', { fte: gainedFTE.toFixed(1) });
    const cta = t('shared.roi.calculator.results.cta', { hours: hoursSavedPerYear.toLocaleString() });

    return { diagnosis, impact, strategy, cta };
  };

  const profile = getPsychologicalProfile();

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="animate-fade-in">
            <div className="mb-12 border-b border-gray-900 pb-6"><h2 className="text-5xl font-bold text-gray-900 tracking-tighter leading-[0.9]">{t('shared.roi.calculator.steps.step1.title_part1')}<br />{t('shared.roi.calculator.steps.step1.title_part2')}</h2></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-gray-200">
              {(Array.isArray(t('shared.roi.calculator.steps.step1.options', { returnObjects: true })) ? t('shared.roi.calculator.steps.step1.options', { returnObjects: true }) as string[] : ['Medicine & Health', 'Law & Taxes', 'Trade & E-Commerce', 'Production & Industry', 'Real Estate', 'Services', 'Craft & Construction', 'Other / Generalist']).map((ind) => (
                <button key={ind} disabled={isNavigating} onClick={() => { setFormData({ ...formData, industry: ind }); nextStep(); }} className={`p-8 text-left border-b border-r border-gray-200 transition-all duration-200 flex items-center justify-between group hover:bg-gray-50 ${formData.industry === ind ? 'bg-gray-100 border-l-4 border-l-[#0B1121] pl-[28px]' : 'text-gray-900'}`}>
                  <span className={`font-bold text-lg tracking-tight ${formData.industry === ind ? 'text-[#0B1121]' : ''}`}>{ind}</span>
                  {formData.industry === ind && <Check size={20} className="text-[#0B1121]" />}
                </button>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="animate-fade-in">
            <div className="mb-12 border-b border-gray-900 pb-6"><h2 className="text-5xl font-bold text-gray-900 tracking-tighter leading-[0.9]">{t('shared.roi.calculator.steps.step2.title_part1')}<br />{t('shared.roi.calculator.steps.step2.title_part2')}</h2></div>
            <div className="flex flex-col border-t border-gray-200">
              {(Array.isArray(t('shared.roi.calculator.steps.step2.options', { returnObjects: true })) ? t('shared.roi.calculator.steps.step2.options', { returnObjects: true }) as string[] : ['Option 1', 'Option 2', 'Option 3', 'Option 4']).map((opt) => (
                <button key={opt} onClick={() => handleMultiSelect(opt, 'status')} className={`w-full py-6 px-4 text-left border-b border-gray-200 transition-all duration-200 flex items-center justify-between group hover:pl-8 ${formData.status.includes(opt) ? 'bg-gray-100' : ''}`}>
                  <div className="flex items-center gap-6">
                    <div className={`w-6 h-6 border border-gray-900 flex items-center justify-center ${formData.status.includes(opt) ? 'bg-[#0B1121] border-[#0B1121] text-white' : 'bg-white'}`}>{formData.status.includes(opt) && <Check size={14} />}</div>
                    <div className={`font-bold text-xl tracking-tight ${formData.status.includes(opt) ? 'text-[#0B1121]' : 'text-gray-900'}`}>{opt}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="animate-fade-in">
            <div className="mb-12 border-b border-gray-900 pb-6"><h2 className="text-5xl font-bold text-gray-900 tracking-tighter leading-[0.9]">{t('shared.roi.calculator.steps.step3.title_part1')}<br />{t('shared.roi.calculator.steps.step3.title_part2')}</h2></div>
            <div className="space-y-4">
              {(Array.isArray(t('shared.roi.calculator.steps.step3.options', { returnObjects: true })) ? t('shared.roi.calculator.steps.step3.options', { returnObjects: true }) as string[] : ['Free up capacity', 'Increase revenue', 'Reduce errors', 'Cut costs']).map((goal) => (
                <button key={goal} onClick={() => handleMultiSelect(goal, 'goals')} className={`w-full p-6 text-left border-2 transition-all duration-200 flex items-center justify-between group ${formData.goals.includes(goal) ? 'border-gray-300 bg-gray-50 text-[#0B1121]' : 'border-gray-200 hover:border-gray-400 text-gray-900'}`}>
                  <span className="font-bold text-lg tracking-wide uppercase">{goal}</span>
                  {formData.goals.includes(goal) && <ArrowRight size={20} className="text-[#0B1121]" />}
                </button>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="animate-fade-in">
            <div className="mb-12 border-b border-gray-900 pb-6"><h2 className="text-5xl font-bold text-gray-900 tracking-tighter leading-[0.9]">{t('shared.roi.calculator.steps.step4.title_part1')}<br />{t('shared.roi.calculator.steps.step4.title_part2')}</h2></div>
            <div className="grid grid-cols-1 gap-0 border-t border-gray-200">
              {(Array.isArray(t('shared.roi.calculator.steps.step4.options', { returnObjects: true })) ? t('shared.roi.calculator.steps.step4.options', { returnObjects: true }) as string[] : ['Immediately', 'Near-term', 'Medium-term', 'Flexible']).map((item) => (
                <button key={item} disabled={isNavigating} onClick={() => { setFormData({ ...formData, speed: item }); nextStep(); }} className={`group py-8 border-b border-gray-200 text-left hover:bg-gray-50 transition-colors flex flex-col items-start ${formData.speed === item ? 'bg-gray-100 border-l-4 border-l-[#0B1121] pl-[16px]' : ''}`}><span className={`text-2xl font-bold tracking-tight mb-2 ${formData.speed === item ? 'text-[#0B1121]' : 'text-gray-900'}`}>{item}</span></button>
              ))}
            </div>
          </div>
        );
      case 5:
        return (
          <div className="animate-fade-in">
            <div className="mb-12 border-b border-gray-900 pb-6"><h2 className="text-5xl font-bold text-gray-900 tracking-tighter leading-[0.9]">{t('shared.roi.calculator.steps.step5.title_part1')}<br />{t('shared.roi.calculator.steps.step5.title_part2')}</h2></div>
            <div className="mb-16">
              <div className="flex justify-between items-end mb-6"><label className="text-lg font-bold text-gray-900 uppercase tracking-wide">{t('shared.roi.calculator.steps.step5.employees_label')}</label><span className="text-4xl font-bold text-[#0B1121] font-mono">{formData.employees}</span></div>
              <input type="range" min="0" max="50" step="1" value={formData.employees} onChange={(e) => setFormData({ ...formData, employees: parseInt(e.target.value) })} className="w-full h-1 bg-gray-200 rounded-none appearance-none cursor-pointer accent-black" />
            </div>
            <div>
              <div className="flex justify-between items-end mb-6"><label className="text-lg font-bold text-gray-900 uppercase tracking-wide">{t('shared.roi.calculator.steps.step5.salary_label')}</label><span className="text-4xl font-bold text-[#0B1121] font-mono">€ {formData.salary.toLocaleString()}</span></div>
              <input type="range" min="0" max="8000" step="100" value={formData.salary} onChange={(e) => setFormData({ ...formData, salary: parseInt(e.target.value) })} className="w-full h-1 bg-gray-200 rounded-none appearance-none cursor-pointer accent-black" />
            </div>
          </div>
        );
      case 6:
        return (
          <div className="animate-fade-in">
            <div className="mb-10 border-b border-gray-900 pb-6"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tighter leading-[0.9]">{t('shared.roi.calculator.steps.step6.title_part1')}<br />{t('shared.roi.calculator.steps.step6.title_part2')}</h2></div>
            <div className="space-y-8">
              {[
                { label: t('shared.roi.calculator.steps.step6.items.emails'), val: formData.emails, set: (v: number) => setFormData({ ...formData, emails: v }), max: 120 },
                { label: t('shared.roi.calculator.steps.step6.items.data'), val: formData.dataRecords, set: (v: number) => setFormData({ ...formData, dataRecords: v }), max: 60 },
                { label: t('shared.roi.calculator.steps.step6.research'), val: formData.searchMinutes, set: (v: number) => setFormData({ ...formData, searchMinutes: v }), max: 60 },
                { label: t('shared.roi.calculator.steps.step6.meetings'), val: formData.meetings, set: (v: number) => setFormData({ ...formData, meetings: v }), max: 60 },
                { label: t('shared.roi.calculator.steps.step6.filing'), val: formData.filing, set: (v: number) => setFormData({ ...formData, filing: v }), max: 60 },
              ].map((item, idx) => (
                <div key={idx} className="group">
                  <div className="flex justify-between items-center mb-3"><div className="font-bold text-gray-900 text-sm uppercase tracking-wider">{item.label}</div><div className="font-mono font-bold text-[#0B1121]">{item.val} <span className="text-gray-400 text-xs">{t('shared.roi.calculator.steps.step6.min_unit')}</span></div></div>
                  <input type="range" min="0" max={item.max} step="5" value={item.val} onChange={(e) => item.set(parseInt(e.target.value))} className="w-full h-px bg-gray-300 appearance-none cursor-pointer accent-black group-hover:h-1 transition-all" />
                </div>
              ))}
            </div>
          </div>
        );
      case 7:
        return (
          <div className="animate-slide-up w-full pb-20">
            <div className="mb-12"><h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-[0.85]">{t('shared.roi.calculator.steps.step7.title_part1')}<br />{t('shared.roi.calculator.steps.step7.title_part2')}</h2></div>
            <div className="border-t border-gray-200">
              <div className="py-10 border-b border-gray-200 grid lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-4"><div className="text-3xl font-bold text-gray-900 font-mono tracking-tighter">{Math.round(burnPercentage)}%</div><div className="text-xs font-bold text-clever-teal uppercase mt-1">{t('shared.roi.calculator.steps.step7.load_label')}</div></div>
                <div className="lg:col-span-8"><p className="text-gray-600 leading-relaxed text-lg">{profile.diagnosis}</p></div>
              </div>
              <div className="py-10 border-b border-gray-200">
                <div className="grid xl:grid-cols-12 gap-8 items-start">
                  <div className="xl:col-span-5"><div className="text-3xl lg:text-5xl font-bold text-clever-teal font-mono tracking-tighter">€ {Math.round(annualBurnRate).toLocaleString()}</div><div className="text-xs font-mono text-gray-400 mt-1">{t('shared.roi.calculator.steps.step7.inefficiency_label')}</div></div>
                  <div className="xl:col-span-7"><p className="text-gray-600 leading-relaxed text-lg">{renderWithBold(profile.impact)}</p></div>
                </div>
              </div>
              <div className="py-10 border-b border-gray-200 grid lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-4"><div className="text-3xl font-bold text-emerald-600 font-mono tracking-tighter">+{gainedFTE.toFixed(1)}</div><div className="text-xs font-bold text-gray-400 uppercase mt-1">{t('shared.roi.calculator.steps.step7.fte_label')}</div></div>
                <div className="lg:col-span-8"><p className="text-gray-600 leading-relaxed text-lg">{profile.strategy}</p><CapacityShiftChart adminPercent={burnPercentage} valuePercent={productivePercentage} /></div>
              </div>
              <div className="py-12 grid md:grid-cols-12 gap-8 items-center bg-[#0B1121] text-white px-6 md:px-10 mt-8 shadow-2xl">
                <div className="md:col-span-8"><h3 className="text-2xl font-bold mb-2">{t('shared.roi.calculator.steps.step7.ready_title')}</h3><p className="text-gray-400 text-sm">{profile.cta}</p></div>
                <div className="md:col-span-4 text-right"><button onClick={() => setIsModalOpen(true)} className="bg-white text-[#0B1121] px-8 py-4 font-bold uppercase tracking-widest text-xs hover:bg-gray-200 transition-colors inline-flex items-center gap-2">{t('shared.roi.calculator.steps.step7.cta_button')} <ChevronRight size={14} /></button></div>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="bg-white min-h-screen font-sans">
      <ROIHero onStart={scrollToCalculator} />
      <div id="calculator-start" className="flex flex-col md:flex-row w-full min-h-screen items-stretch">
        {/* LEFT PANEL */}
        <div className="w-full md:w-1/2 p-6 md:p-16 flex flex-col justify-between min-h-[90vh] bg-white border-r border-gray-100 relative">
          <div className="mb-12 flex flex-col gap-2">
            <div className="flex justify-between text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400"><span>Phase {currentStep}</span><span>07</span></div>
            <div className="h-1 w-full bg-gray-100"><div className="h-full bg-[#0B1121] transition-all duration-500 ease-out" style={{ width: `${(currentStep / 7) * 100}%` }}></div></div>
          </div>
          <div className="flex-1 flex flex-col justify-center max-w-lg mx-auto w-full">{renderStepContent()}</div>
          <div className="mt-12 flex justify-between items-center pt-8 border-t border-gray-50">
            {currentStep > 1 && !isNavigating ? (
              <button onClick={prevStep} className="flex items-center gap-2 text-gray-400 hover:text-black font-bold uppercase text-xs tracking-[0.2em] transition-colors group"><ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> {t('shared.roi.calculator.nav.back')}</button>
            ) : <div></div>}
            {currentStep < 7 && !isNavigating && (
              <button onClick={nextStep} className="flex items-center gap-3 bg-[#0B1121] text-white px-8 py-4 font-bold uppercase tracking-[0.1em] text-xs hover:bg-[#1a253a] transition-colors">{t('shared.roi.calculator.nav.next')} <ArrowRight size={14} /></button>
            )}
            {isNavigating && <div className="flex items-center gap-2 text-gray-300 font-mono text-[10px] uppercase tracking-widest"><Loader2 size={12} className="animate-spin" /> {t('shared.roi.calculator.nav.loading')}</div>}
          </div>
        </div>

        {/* RIGHT PANEL: BLUEPRINT MONITOR */}
        <div className="w-full md:w-1/2 bg-[#0B1121] text-white p-6 md:p-16 flex flex-col justify-start sticky top-0 h-screen overflow-y-auto custom-scrollbar border-l border-white/5">
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          <div className="max-w-lg mx-auto w-full space-y-8 relative z-10 pt-6 pb-12 md:pt-10">
            <div className="border-b border-gray-800 pb-4"><h2 className="text-3xl font-bold text-white tracking-tight uppercase tracking-[0.1em]">{t('shared.roi.calculator.results.title')}</h2></div>
            <div className="bg-[#0F172A] border border-gray-700 p-6 md:p-8 shadow-2xl relative">
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-white"></div><div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-white"></div>
              {totalPayroll === 0 ? (
                <div className="h-64 flex flex-col items-center justify-center text-gray-600 space-y-4 font-mono text-xs"><div className="w-16 h-16 border border-dashed border-gray-700 flex items-center justify-center"><Target size={24} /></div><div className="uppercase tracking-widest">{t('shared.roi.calculator.results.waiting')}</div></div>
              ) : (
                <div className="animate-fade-in space-y-6">
                  <div><div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1 font-mono">{t('shared.roi.calculator.results.total_investment')}</div><div className="text-3xl font-bold text-white font-mono">€ {Math.round(totalPayroll).toLocaleString()}</div></div>
                  <div className="relative w-full h-12 border border-gray-600 flex bg-black/50 overflow-hidden">
                    <div className="h-full bg-emerald-500 relative border-r border-black/20" style={{ width: `${productivePercentage}%`, transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)' }}><div className="absolute bottom-1 left-2 text-[8px] font-bold text-[#0B1121] uppercase tracking-widest">{t('shared.roi.calculator.results.value_label')}</div></div>
                    <div className="h-full relative bg-clever-teal" style={{ width: `${burnPercentage}%`, transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)', backgroundImage: 'linear-gradient(45deg, rgba(0,0,0,0.1) 25%, transparent 25%, transparent 50%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.1) 75%, transparent 75%, transparent)', backgroundSize: '6px 6px' }}><div className="absolute bottom-1 right-2 text-[8px] font-bold text-white uppercase tracking-widest">{t('shared.roi.calculator.results.admin_label')}</div></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-800">
                    <div><div className="text-gray-500 text-[10px] uppercase tracking-widest mb-1">{t('shared.roi.calculator.results.load_label')}</div><div className="text-xl font-bold text-clever-teal font-mono">{Math.round(burnPercentage)}%</div></div>
                    <div><div className="text-gray-500 text-[10px] uppercase tracking-widest mb-1">{t('shared.roi.calculator.results.recovery_label')}</div><div className="text-xl font-bold text-emerald-500 font-mono">€ {Math.round(potentialSavings).toLocaleString()}</div></div>
                    <div className="col-span-2"><div className="text-gray-500 text-[10px] uppercase tracking-widest mb-1">{t('shared.roi.calculator.results.capacity_label')}</div><div className="text-2xl font-bold text-white font-mono">{hoursSavedPerYear.toLocaleString()} h <span className="text-xs text-gray-500">/ {t('shared.roi.calculator.results.year_unit')}</span></div></div>
                  </div>
                </div>
              )}
            </div>
            <div className="text-xs text-gray-400 leading-relaxed border-l-2 border-clever-teal pl-6 py-2 font-light">
              {totalPayroll > 0 ? (
                <>
                  <div className="font-bold text-white mb-2 uppercase tracking-widest">{t('shared.roi.calculator.results.basis_title')}:</div>
                  {t('shared.roi.calculator.results.basis_text', {
                    employees: formData.employees,
                    minutes: wastedMinutesPerDayPerEmployee,
                    automation: automationFactor * 100,
                    hours: hoursSavedPerYear.toLocaleString(),
                    fte: gainedFTE.toFixed(1)
                  })}
                </>
              ) : (
                t('shared.roi.calculator.results.waiting_init')
              )}
            </div>
          </div>
        </div>
      </div>

      <ROIQuoteSection />
      <ROIProcess onOpenModal={() => setIsModalOpen(true)} />
      <ROIFAQ />

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-6 animate-fade-in">
          <div className="bg-white w-full max-w-2xl shadow-2xl relative overflow-hidden">
            <button onClick={() => setIsModalOpen(false)} className="absolute top-6 right-6 text-gray-400 hover:text-black"><X size={24} /></button>
            <div className="p-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-2 tracking-tighter">{t('shared.roi.contact_modal.title')}</h3>
              <p className="text-gray-500 mb-8 font-mono text-xs uppercase tracking-widest">{t('shared.roi.contact_modal.subtitle')}</p>
              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center"><div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6"><Check size={32} /></div><h4 className="text-2xl font-bold text-gray-900 mb-2">{t('shared.roi.contact_modal.success_title')}</h4><button onClick={() => setIsModalOpen(false)} className="mt-8 text-sm font-bold underline hover:text-clever-teal">{t('shared.roi.contact_modal.close')}</button></div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t('shared.roi.contact_modal.form.first_name_label')}</label><input required className="w-full bg-gray-50 border border-gray-200 p-4" placeholder={t('shared.roi.contact_modal.form.placeholders.first_name')} value={contactForm.firstName} onChange={e => setContactForm({ ...contactForm, firstName: e.target.value })} /></div>
                    <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t('shared.roi.contact_modal.form.last_name_label')}</label><input required className="w-full bg-gray-50 border border-gray-200 p-4" placeholder={t('shared.roi.contact_modal.form.placeholders.last_name')} value={contactForm.lastName} onChange={e => setContactForm({ ...contactForm, lastName: e.target.value })} /></div>
                  </div>
                  <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t('shared.roi.contact_modal.form.company_label')}</label><input required className="w-full bg-gray-50 border border-gray-200 p-4" placeholder={t('shared.roi.contact_modal.form.placeholders.company')} value={contactForm.company} onChange={e => setContactForm({ ...contactForm, company: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-6">
                    <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t('shared.roi.contact_modal.form.email_label')}</label><input required type="email" className="w-full bg-gray-50 border border-gray-200 p-4" placeholder={t('shared.roi.contact_modal.form.placeholders.email')} value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} /></div>
                    <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t('shared.roi.contact_modal.form.phone_label')}</label><input required type="tel" className="w-full bg-gray-50 border border-gray-200 p-4" placeholder={t('shared.roi.contact_modal.form.placeholders.phone')} value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} /></div>
                  </div>
                  <button type="submit" disabled={formStatus === 'submitting'} className="w-full bg-[#0B1121] text-white py-5 font-bold uppercase hover:bg-gray-900 transition-colors flex items-center justify-center gap-3 disabled:opacity-70">{formStatus === 'submitting' ? <><Loader2 className="animate-spin" size={20} /> {t('shared.roi.contact_modal.submitting')}</> : <>{t('shared.roi.contact_modal.submit')} <ArrowRight size={20} /></>}</button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
